'use client';

import {
  createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo,
} from 'react';
import { useMcHistory } from '@hooks/history/useMcHistory';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import { slugify } from '@utils/strings/slugify';
import { buildKeywordWithSalary, extractSalaryFromKeyword } from '@utils/strings/keywordSalary';

export type CandidateSearchFilters = {
  keyword?: string;
  locations?: string[];
  salary?: number;
};

export type SidebarView =
  | 'new-search'
  | 'new-job-search-form'
  | 'my-bestie-landing'
  | 'browse-all'
  | { keyword: string }
  | { candidateSearch: CandidateSearchFilters }
  | { editSearch: CandidateSearchFilters };

interface SidebarNavigationContextValue {
  currentView: SidebarView;
  setView: (view: SidebarView) => void;
  navigateToNewSearch: () => void;
  navigateToNewJobSearchForm: () => void;
  navigateToMyBestieLanding: () => void;
  navigateToBrowseAll: () => void;
  navigateToKeywordSearch: (keyword: string) => void;
  navigateToCandidateSearch: (filters: CandidateSearchFilters) => void;
  navigateToEditSearch: (filters: CandidateSearchFilters) => void;
}

const SidebarNavigationContext = createContext<SidebarNavigationContextValue | undefined>(undefined);

interface SidebarNavigationProviderProps {
  children: ReactNode;
  initialView?: SidebarView;
  locale: Locale;
}

/** Derives the sidebar view from a path + search string (used for popstate/back-forward). */
const getViewFromUrl = (path: string, search: string): SidebarView => {
  const urlParams = new URLSearchParams(search);
  const isEditMode = urlParams.get('edit') === '1';

  // Backward compat: old keyword-search URL — treat as candidateSearch
  const keywordSearchMatch = path.match(/\/mc\/job-search\/([^/]+)/);
  if (keywordSearchMatch) {
    const keyword = decodeURIComponent(keywordSearchMatch[1]).replace(/-/g, ' ');
    return { candidateSearch: { keyword } };
  }

  // Candidate search/edit results: /mc/locuri-de-munca[-in-CITY][/KEYWORD] or /mc/jobs[-in-CITY][/KEYWORD]
  const candidateListMatch = path.match(/\/mc\/(?:locuri-de-munca|jobs)(?:-in-([^/]+?))?(?:\/([^/?]+))?(?=[/?]|$)/);
  if (candidateListMatch) {
    const locationSlug = candidateListMatch[1];
    const keywordSlug = candidateListMatch[2];
    const rawKeyword = keywordSlug ? decodeURIComponent(keywordSlug).replace(/-/g, ' ') : undefined;
    const { keyword, salary } = extractSalaryFromKeyword(rawKeyword);
    const locations = locationSlug ? locationSlug.split(',').map((s) => decodeURIComponent(s)) : undefined;
    const filters = {
      ...(keyword && { keyword }),
      ...(locations?.length && { locations }),
      ...(salary && { salary }),
    };
    // ?edit=1 signals the edit form should be shown, not results
    return isEditMode ? { editSearch: filters } : { candidateSearch: filters };
  }

  if (/\/mc\/my-bestie/.test(path)) {
    return 'my-bestie-landing';
  }

  if (/\/mc\/search/.test(path)) {
    const rawKeyword = urlParams.get('keyword') ?? undefined;
    const { keyword, salary } = extractSalaryFromKeyword(rawKeyword);
    const locations = urlParams.getAll('location');
    if (keyword || locations.length || salary) {
      return {
        editSearch: {
          ...(keyword && { keyword }),
          ...(locations.length && { locations }),
          ...(salary && { salary }),
        },
      };
    }
    return 'new-job-search-form';
  }

  return 'new-search';
};

/**
 * @description Provider for sidebar navigation state management.
 *
 * View state is initialised from the `initialView` prop (set by the server page component
 * based on the current URL). Client-side navigation calls `setView` which updates state
 * immediately and then pushes the matching URL. Browser back/forward is handled via a
 * `popstate` listener so the effect cannot race with explicit `setView` calls.
 */
export const SidebarNavigationProvider = (props: SidebarNavigationProviderProps) => {
  const { children, initialView = 'new-search', locale } = props;

  const [currentView, setCurrentView] = useState<SidebarView>(initialView);
  const { push, getCurrentPathname } = useMcHistory(locale);
  const { router } = useExtendedRouter();

  // Build the MC URL that represents a given sidebar view.
  const buildViewUrl = useCallback((view: SidebarView): string => {
    if (view === 'new-search' || view === 'browse-all') return getI18nLinkHref('/mc', locale);
    if (view === 'my-bestie-landing') return getI18nLinkHref('/mc/my-bestie', locale);
    if (view === 'new-job-search-form') return getI18nLinkHref('/mc/search', locale);
    if (typeof view === 'object' && 'keyword' in view) {
      return getI18nLinkHref(`/mc/job/list/${slugify(view.keyword)}`, locale);
    }

    const isEdit = typeof view === 'object' && 'editSearch' in view;
    const filters = isEdit ? view.editSearch : (view as { candidateSearch: CandidateSearchFilters }).candidateSearch;
    const { keyword, locations, salary } = filters;
    const effectiveKeyword = buildKeywordWithSalary(keyword, salary);
    const keywordSlug = effectiveKeyword ? `/${slugify(effectiveKeyword)}` : '';

    const base = locations?.length
      ? getI18nLinkHref(`/mc/job/list/location-${slugify(locations.join(','))}`, locale).replace(/%2C/g, ',')
      : getI18nLinkHref('/mc/job/list', locale);

    return `${base}${keywordSlug}${isEdit ? '?edit=1' : ''}`;
  }, [locale]);

  // Update URL using native history — avoids a Next.js navigation that would remount the component tree.
  // Hard reloads of any MC URL still get the correct initialView from the server page component.
  // Browser back/forward is handled separately via the popstate listener.
  const updateURL = useCallback((view: SidebarView) => {
    push(buildViewUrl(view), {});
  }, [push, buildViewUrl]);

  // Set view and update URL. On MC pages the lightweight pushState keeps the sidebar mounted;
  // off-MC (e.g. /profile), a Next.js navigation is required so the MC content actually loads.
  const setView = useCallback((view: SidebarView) => {
    const onMcPath = /\/mc(\/|$)/.test(getCurrentPathname());
    if (!onMcPath) {
      router.push(buildViewUrl(view), { withLocale: false });
      return;
    }
    setCurrentView(view);
    updateURL(view);
  }, [updateURL, buildViewUrl, getCurrentPathname, router]);

  // Sync view on browser back/forward navigation (popstate only — never races with setView).
  useEffect(() => {
    const handlePopState = () => {
      setCurrentView(getViewFromUrl(getCurrentPathname(), window.location.search));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [getCurrentPathname]);

  // Convenience methods
  const navigateToNewSearch = useCallback(() => setView('new-search'), [setView]);
  const navigateToNewJobSearchForm = useCallback(() => setView('new-job-search-form'), [setView]);
  const navigateToMyBestieLanding = useCallback(() => setView('my-bestie-landing'), [setView]);
  const navigateToBrowseAll = useCallback(() => setView('browse-all'), [setView]);
  const navigateToKeywordSearch = useCallback((keyword: string) => setView({ keyword }), [setView]);
  const navigateToCandidateSearch = useCallback((filters: CandidateSearchFilters) => setView({ candidateSearch: filters }), [setView]);
  const navigateToEditSearch = useCallback((filters: CandidateSearchFilters) => setView({ editSearch: filters }), [setView]);

  const value: SidebarNavigationContextValue = useMemo(() => ({
    currentView,
    setView,
    navigateToNewSearch,
    navigateToNewJobSearchForm,
    navigateToMyBestieLanding,
    navigateToBrowseAll,
    navigateToKeywordSearch,
    navigateToCandidateSearch,
    navigateToEditSearch,
  }), [
    currentView, setView, navigateToNewSearch, navigateToNewJobSearchForm,
    navigateToMyBestieLanding, navigateToBrowseAll,
    navigateToKeywordSearch, navigateToCandidateSearch, navigateToEditSearch,
  ]);

  return (
    <SidebarNavigationContext.Provider value={value}>
      {children}
    </SidebarNavigationContext.Provider>
  );
};

/**
 * @description Hook to access sidebar navigation context.
 * Throws error if used outside of SidebarNavigationProvider.
 */
export const useSidebarNavigation = () => {
  const context = useContext(SidebarNavigationContext);

  if (!context) {
    throw new Error('useSidebarNavigation must be used within SidebarNavigationProvider');
  }

  return context;
};

/**
 * @description Optional variant used by components that can render both inside and
 * outside the SidebarNavigationProvider (e.g. standalone MC pages).
 */
export const useSidebarNavigationOptional = () => useContext(SidebarNavigationContext);
